import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import { LABELS } from "../../platform/config";
import { ConsentForm, listConsentForms, saveMedicalInfo } from "../../services/clientRecordsService";
import MedicalInfoFields, { MedicalInfoValues, emptyMedicalInfo, isMedicalInfoEmpty } from "./MedicalInfoFields";
import ConsentSignPanel from "./ConsentSignPanel";

interface Props {
  /** The client just created; null keeps the dialog closed. */
  client: { id: string; fullName: string } | null;
  onClose: () => void;
}

/**
 * Right after a client is created: optional medical / health info and the
 * organization's consent forms. Everything can be skipped and filled in
 * later from the client's file.
 */
const ClientIntakeDialog: React.FC<Props> = ({ client, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [medical, setMedical] = useState<MedicalInfoValues>(emptyMedicalInfo());
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [signedIds, setSignedIds] = useState<string[]>([]);
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) return;
    setMedical(emptyMedicalInfo());
    setSignedIds([]);
    setOpenForm(null);
    listConsentForms()
      .then(setForms)
      .catch(() => setForms([]));
  }, [client]);

  const finish = async () => {
    if (!client) return;
    if (isMedicalInfoEmpty(medical)) return onClose();
    setSaving(true);
    try {
      await saveMedicalInfo(client.id, medical);
      toast.success(`${LABELS.medicalInfo} saved.`, { position: "top-right" });
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error((Array.isArray(message) ? message[0] : message) || "Could not save.", {
        position: "top-right",
      });
    }
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(client)} onClose={() => !saving && onClose()} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {client?.fullName}: {LABELS.medicalInfo.toLowerCase()} and consent
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", fontWeight: 400 }}>
          Optional. You can skip this and fill it in later from the {LABELS.clientFile.toLowerCase()}.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{LABELS.medicalInfo}</Typography>
        <MedicalInfoFields value={medical} onChange={setMedical} />

        {forms.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Consent forms</Typography>
            {forms.map((form) => {
              const signed = signedIds.includes(form.id);
              return (
                <Box key={form.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, mb: 1.25 }}>
                  {openForm === form.id && client ? (
                    <ConsentSignPanel
                      clientId={client.id}
                      form={form}
                      defaultName={client.fullName}
                      onCancel={() => setOpenForm(null)}
                      onSigned={() => {
                        setSignedIds((prev) => [...prev, form.id]);
                        setOpenForm(null);
                      }}
                    />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{form.title}</Typography>
                      {signed ? (
                        <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Signed" />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => setOpenForm(form.id)} sx={{ textTransform: "none" }}>
                          Sign now
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", mr: "auto" }}>
          Skip for now
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={finish}
          disabled={saving || openForm !== null}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none" }}
        >
          {isMedicalInfoEmpty(medical) ? "Done" : "Save and finish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientIntakeDialog;
